import { Component, computed, ContentChild, inject, input, OnChanges, SimpleChanges, TemplateRef, viewChild } from '@angular/core';
import { SelectedChoiceForDescription } from '../../models/ordering/ordered-choice.model';
import { OrderedItem } from '../../models/ordering/ordered-item.model';
import { OrderableItem } from '../../models/menu/orderable-item.model';
import { CommonModule } from '@angular/common';
import { ConfigService } from '../../services/config.service';
import { ExpandableComponent } from "../../../shared/expandable-component/expandable.component";
import { IconNameType } from '../../../shared/icons/icon-name-type.enum';
import { IconComponent } from '../../../shared/icons/icon.component';
import { TranslocoPipe, TranslocoService } from '@ngneat/transloco';
import { OrderableIngredient } from '../../models/menu/orderable-ingredient.model';
import { OrderedItemService } from '../../services/ordering/ordered-item.service';
import { SizePrice } from '../../models/menu/size-price.model';
import { Utils } from '../../utils/utils';

@Component({
  selector: 'cwo-item-editor-header',
  standalone: true,
  imports: [CommonModule, ExpandableComponent, IconComponent, TranslocoPipe],
  templateUrl: './item-editor-header.component.html',
  styleUrl: './item-editor-header.component.scss'
})
export class ItemEditorHeaderComponent implements OnChanges {
  @ContentChild('details') details?: TemplateRef<any>;
  readonly expandable = viewChild<ExpandableComponent>('expandable');
  
  orderedItemService = inject(OrderedItemService);
  private translocoService = inject(TranslocoService);

  storeConfig = inject(ConfigService).configState().store;
  orderedItem = input<OrderedItem | undefined>();
  orderableItem = input<OrderableItem>();
  showItemNameOnSpecialSingeStep = input(false);

  defaultIngredients: OrderableIngredient[] = [];
  detailsExpanded = false;

  iconName = IconNameType;
  name = input.required<string>();
  size = input<string>();
  loyaltyPoints = input<number | null>(null);

  noticePeriodNoSize = computed<string | undefined>(() => {
    const sizePrices = this.orderableItem()?.sizePrices;
    if (!sizePrices) return undefined;
    if (!Utils.allNoticePeriodsSame(sizePrices)) return undefined;
    return Utils.getNoticePeriodLabel(sizePrices, (key, params) => this.translocoService.translate(key, params));
  });
  
  hasCalories = computed(() => {
    const sizePrices = this.orderableItem()?.sizePrices;
    if (!sizePrices) return null;
    const targetSize = this.orderedItem()?.size;
    const sizeWithCalories = sizePrices.find((s: SizePrice) => s.size === targetSize);
    if (!sizeWithCalories) return null;
    return Utils.getCalorieDisplay(this.orderableItem(), sizeWithCalories); 
  });

  updateExpanded(value: boolean) {
    if(this.detailsExpanded && !value) {
      this.expandable()?.toggleExpanded(undefined);
    } else if(!this.detailsExpanded && value) {      
      this.expandable()?.toggleExpanded(undefined);
    }    
  }
  
  selectedIngredients = computed<SelectedChoiceForDescription[]>(() => {
    const visiblechoices = this.identifyVisibleChoices();
    const visibleIngredients = ([] as OrderableIngredient[]).concat(...visiblechoices.map(choice => choice.ingredients)).map(ingredient => ingredient.name);

    return [
      ...this.defaultIngredients
      .filter((ingredient) => !this.orderedItem()?.choices.flatMap((choice) => choice.ingredients).find((ing) => ing.ingredient === ingredient.name && (ing.isLeftHalf || ing.isRightHalf || ing.qualifiers?.length)))
      .map((ingredient) => ({
        label: ingredient.name,
        default: true,
        removed: !this.orderedItem()?.choices.flatMap((choice) => choice.ingredients).find((ing) => ing.ingredient === ingredient.name),
      })),
      ...this.orderedItem()?.choices.flatMap((choice) => choice.ingredients.filter((ingredient) => !this.defaultIngredients.find((defaultIngredient) => (defaultIngredient.name === ingredient.ingredient && !(ingredient.isLeftHalf || ingredient.isRightHalf || ingredient.qualifiers?.length))))
      .map((ingredient) => ({
        label: ingredient.ingredient,
        removed: false,
      }))) ?? []
    ]
    .filter(ingredient => visibleIngredients.indexOf(ingredient.label ?? '') !== -1)
    .sort((a, b) => a.removed === b.removed ? 0 : b.removed ? 1 : -1);
  });

  printableIngredients = computed(() => {
    return this.selectedIngredients().filter(i => !(this.orderableItem()?.description && i.default && !i.removed))
  })

  ngOnChanges( changes: SimpleChanges) { 
    if(changes['orderableItem']){
      this.defaultIngredients = this.orderableItem()?.getDefaultIngredients() ?? [];
    }
  }

  private identifyVisibleChoices() {
    if (!(this.orderableItem() && this.orderedItem())) {
      return [];
    }
    return this.orderedItemService.getFilteredIngredientChoices(this.orderableItem()!, this.orderedItem()!);
  }

  getSelectedIngredientDescription(ingredient: SelectedChoiceForDescription): string {
    const orderedIngredient = this.orderedItem()?.choices.flatMap((choice) => choice.ingredients).find((ing) => ing.ingredient === ingredient.label);

    let descrtiption = '';
    if(!orderedIngredient) {
      return ingredient.label ?? '';
    }

    if(orderedIngredient.isRightHalf && !orderedIngredient.isLeftHalf){
      descrtiption += 'Right half ';
    }

    if(orderedIngredient.isLeftHalf && !orderedIngredient.isRightHalf){
      descrtiption += 'Left half ';
    }

    if(orderedIngredient.qualifiers.length){
      descrtiption += orderedIngredient.qualifiers.join(' ');
      descrtiption += ' ';
    }

    descrtiption += orderedIngredient.ingredient;

    if(orderedIngredient.isSecondHalf){
      if (orderedIngredient.isLeftHalf && !orderedIngredient.isRightHalf) {
      descrtiption += ', Right half ';}
      else if (orderedIngredient.isRightHalf && !orderedIngredient.isLeftHalf){
        descrtiption += ', Left half ';
      }
      if(orderedIngredient.secondHalfQualifiers.length){
        descrtiption += orderedIngredient.secondHalfQualifiers.join(' ');
        descrtiption += ' ';
      }
      descrtiption += orderedIngredient.ingredient;
    }

    return descrtiption;

  }

  handleDetailsToggle(value: boolean) {
    this.detailsExpanded = value;
  }

}
